import React, { useEffect } from 'react';
import { FaSearch, FaEye, FaEyeSlash } from 'react-icons/fa';

const AddUserModal = ({
  showAddModal,
  setShowAddModal,
  newUser,
  setNewUser,
  showPassword,
  setShowPassword,
  isSubmitting,
  companySearch,
  setCompanySearch,
  filteredCompanies,
  loadingCompanies,
  handleAddSubmit,
  handleCompanyCheckboxChange,
  companies
}) => {
  // Reset search when modal opens
  useEffect(() => {
    if (showAddModal) {
      setCompanySearch('');
    }
  }, [showAddModal, setCompanySearch]);

  // Handle Select All companies
  const handleSelectAllCompanies = () => {
    if (filteredCompanies.length === 0) return;
    
    const allCompanyIds = filteredCompanies.map(company => company.id);
    const currentCompanyIds = new Set(newUser.company_ids);
    
    // Add only companies that are in filtered results and not already selected
    const newCompanyIds = [...newUser.company_ids];
    filteredCompanies.forEach(company => {
      if (!currentCompanyIds.has(company.id)) {
        newCompanyIds.push(company.id);
      }
    });
    
    setNewUser({...newUser, company_ids: newCompanyIds});
  };

  // Handle Clear All companies
  const handleClearAllCompanies = () => {
    if (newUser.company_ids.length === 0) return;
    
    // Remove only companies that are in filtered results
    const filteredCompanyIds = new Set(filteredCompanies.map(company => company.id));
    const remainingCompanyIds = newUser.company_ids.filter(
      companyId => !filteredCompanyIds.has(companyId)
    );
    
    setNewUser({...newUser, company_ids: remainingCompanyIds});
  };

  // Check if all filtered companies are selected
  const areAllFilteredCompaniesSelected = () => {
    if (filteredCompanies.length === 0) return false;
    
    const filteredCompanyIds = new Set(filteredCompanies.map(company => company.id));
    const selectedCompanyIds = new Set(newUser.company_ids);
    
    return filteredCompanies.every(company => selectedCompanyIds.has(company.id));
  };

  // Check if any filtered companies are selected
  const areAnyFilteredCompaniesSelected = () => {
    if (filteredCompanies.length === 0) return false;
    
    const filteredCompanyIds = new Set(filteredCompanies.map(company => company.id));
    const selectedCompanyIds = new Set(newUser.company_ids);
    
    return filteredCompanies.some(company => selectedCompanyIds.has(company.id));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Add New User</h3>
        <form onSubmit={handleAddSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email *
              </label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="Enter email address"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Enter password (min 8 characters)"
                  required
                  minLength="8"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Password must be at least 8 characters long
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newUser.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setNewUser({...newUser, role: newRole});
                  if (newRole === 'superadmin') {
                    setNewUser(prev => ({...prev, company_ids: []}));
                  }
                }}
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>
            
            {newUser.role !== 'superadmin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assign Companies (Optional)
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    - {newUser.role === 'admin' ? 'This admin will manage selected companies' : 'This user will only access selected companies'}
                  </span>
                </label>
                
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="text"
                      placeholder="Search companies..."
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                    />
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleSelectAllCompanies}
                      disabled={filteredCompanies.length === 0 || areAllFilteredCompaniesSelected()}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-lg transition-colors whitespace-nowrap"
                      title="Select all companies in current search"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllCompanies}
                      disabled={!areAnyFilteredCompaniesSelected()}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors whitespace-nowrap"
                      title="Clear all companies in current search"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                  {loadingCompanies ? (
                    <div className="p-4 text-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Loading companies...</p>
                    </div>
                  ) : filteredCompanies.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                      {companySearch ? 'No companies found matching your search' : 'No companies available'}
                    </div>
                  ) : (
                    <>
                      {/* Header with select all checkbox */}
                      <div className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600 z-10">
                        <div className="flex items-center px-3 py-2">
                          <input
                            type="checkbox"
                            id="select-all-companies"
                            checked={areAllFilteredCompaniesSelected()}
                            onChange={() => {
                              if (areAllFilteredCompaniesSelected()) {
                                handleClearAllCompanies();
                              } else {
                                handleSelectAllCompanies();
                              }
                            }}
                            disabled={filteredCompanies.length === 0}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <label 
                            htmlFor="select-all-companies" 
                            className="ml-3 flex-1 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300"
                            onClick={(e) => {
                              e.preventDefault();
                              if (areAllFilteredCompaniesSelected()) {
                                handleClearAllCompanies();
                              } else {
                                handleSelectAllCompanies();
                              }
                            }}
                          >
                            {areAllFilteredCompaniesSelected() ? 'Deselect All' : 'Select All'} 
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              ({filteredCompanies.length} company{filteredCompanies.length !== 1 ? 's' : ''})
                            </span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Company list */}
                      {filteredCompanies.map((company) => (
                        <div
                          key={company.id}
                          className={`flex items-center px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                            newUser.company_ids.includes(company.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            id={`company-${company.id}`}
                            checked={newUser.company_ids.includes(company.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCompanyCheckboxChange(company.id, false);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <label 
                            htmlFor={`company-${company.id}`} 
                            className="ml-3 flex-1 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              handleCompanyCheckboxChange(company.id, false);
                            }}
                          >
                            <div className="text-sm text-gray-900 dark:text-white">{company.name}</div>
                            {company.ticker_id && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Ticker: {company.ticker_id}
                              </div>
                            )}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                
                {newUser.company_ids.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Selected: {newUser.company_ids.length} company(s)
                      <button
                        type="button"
                        onClick={() => setNewUser({...newUser, company_ids: []})}
                        className="ml-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Clear All Selections
                      </button>
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {newUser.company_ids.map(companyId => {
                        const company = companies.find(c => c.id === companyId);
                        return company ? (
                          <span 
                            key={companyId}
                            className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded"
                          >
                            {company.name}
                            <button
                              type="button"
                              className="ml-1 text-blue-600 hover:text-blue-800"
                              onClick={() => handleCompanyCheckboxChange(companyId, false)}
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                checked={newUser.is_active}
                onChange={(e) => setNewUser({...newUser, is_active: e.target.checked})}
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Account Active
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setCompanySearch('');
                setNewUser({ 
                  email: '', 
                  password: '',
                  role: 'user',
                  is_active: true,
                  company_ids: []
                });
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !newUser.email.trim() || !newUser.password.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin mr-2">⟳</span>
                  Creating...
                </>
              ) : (
                'Add User'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;